/**
 * Public, browser-reachable base URL of the FastAPI backend.
 *
 * Used for auth flows the browser hits directly (login/logout redirects and the
 * /auth/me check) — distinct from the server-side `API_URL` in `lib/server.ts`.
 * As a `NEXT_PUBLIC_*` var it is inlined at build time, so prod builds must set
 * it as a build arg (see web/Dockerfile). Defaults to localhost for local dev.
 */
export const backendBaseUrl =
	process.env.NEXT_PUBLIC_BACKEND_URL ?? "http://localhost:8000";

/**
 * Origin of the template-engine OnlyOffice editor the Templates workspace embeds (render-only,
 * via postMessage). In local dev it runs as its own static server; in prod the editor's built
 * output is served from this app's own origin under `/office`, so set this to `/office`. A
 * `NEXT_PUBLIC_*` var, inlined at build time.
 */
export const templateEngineBase =
	process.env.NEXT_PUBLIC_TEMPLATE_ENGINE_URL ?? "http://localhost:3940";

/** The engine's origin, for checking `e.origin` and as the postMessage target. Prod's base
 * is the RELATIVE "/office", where `new URL(base)` throws; resolving it against our own
 * origin is what keeps the check on there. Browser only (reads window.location). */
export function templateEngineOrigin(): string {
	return new URL(templateEngineBase, window.location.origin).origin;
}

/**
 * Global kill-switch for the agentic web-search tool. OFF unless the build sets
 * NEXT_PUBLIC_WEB_SEARCH_ENABLED="true". A NEXT_PUBLIC_* build arg (inlined at build time into
 * both the client bundle and the server routes), and staging and prod build from separate
 * workflows (.github/workflows/web-cicd*.yml), so its value is fixed per environment: merging
 * through staging → main never flips it. Default OFF means enabling an environment is a
 * reviewed edit to its web-cicd*.yml, not a silent config change. Staging and prod are both ON
 * (the composer warns before each opt-in that search leaves the firm, so client data must not
 * be in the chat). Read by the composer toggle + the server-side gate.
 *
 * NOTE: the server-side gate runs in the run-engine WORKER, an esbuild bundle outside the Next
 * build, so it reads this (like every NEXT_PUBLIC_* here) from the RUNTIME env. web/Dockerfile's
 * runner stage must therefore set these as ENV too; when it didn't, the toggle was live in the
 * browser while the worker mounted no webSearch tool.
 */
export const webSearchEnabled =
	process.env.NEXT_PUBLIC_WEB_SEARCH_ENABLED === "true";

// Server-side kill-switch for the /api/chat `runCode` tool (native Python with pandas/numpy/
// matplotlib on uploaded data + charts). Default OFF, ON for local dev (docker-compose). Enable
// per environment with CHAT_CODE_EXEC=true (a runtime env, no rebuild). The code runs in the
// py-sandbox service (PY_SANDBOX_URL, lib/agent/ai/python-runner.ts), never in this process:
// without one, the tool is mounted but reports itself unavailable. The fill flow's
// TEMPLATE_PYTHON_EXEC / runPython tool went with the retired /api/fill route.
export const codeExecEnabled = process.env.CHAT_CODE_EXEC === "true";

// Server-side kill-switch for the /api/chat template tools (listTemplates / getTemplateSlots /
// fillTemplate) — lets the MAIN chat agent discover and fill document templates (PDF forms
// today) and return the filled file for download, combining with runCode (parse an Excel) and
// iManage (pull data). Default ON; CHAT_TEMPLATE_FILL=false disables. No heavy runtime deps
// (pdf-lib is isomorphic), so unlike code exec this is safe to leave on everywhere.
export const templateFillEnabled = process.env.CHAT_TEMPLATE_FILL !== "false";
